/**
 * AgentService — the framework-agnostic brain behind the Alinq app screens.
 *
 * It wraps @alinq/sdk (identity, sessions, PSI scheduling), the directory, and the
 * connect flow into the operations the UI calls: onboard, issue a pairing code,
 * connect to a peer, accept/decline requests, manage the network by tier, and run a
 * zero-knowledge scheduling match. Keys live only here (on the device); nothing
 * private ever goes to a server.
 *
 * It is pure TypeScript with injected dependencies (directory + relay transport), so
 * the full journey is unit-tested headlessly. React components are thin views over it.
 */
import { randomBytes } from "@noble/ciphers/webcrypto";
import type {
  DID,
  Message,
  PairingCode,
  SignedAgentCard,
  SignedRelationship,
  TrustTier,
} from "@alinq/proto";
import { TRUST_TIER_NAMES } from "@alinq/proto";
import {
  AlinqClient,
  acceptLink,
  b64uEncode,
  buildLinkRequest,
  counterSign,
  createIdentity,
  decodeInvite,
  downgradeRelationship,
  encodeInvite,
  makeHandle,
  makePairingCode,
  signCard,
  verifyCard,
  verifyCodeProof,
  type ClientTransport,
  type Identity,
} from "@alinq/sdk";

export interface DirectoryClient {
  register(card: SignedAgentCard): Promise<void>;
  resolveHandle(handle: string): Promise<SignedAgentCard | undefined>;
  resolveDid(did: string): Promise<SignedAgentCard | undefined>;
}

export interface AgentServiceDeps {
  directory: DirectoryClient;
  connectToRelay: () => ClientTransport;
  now?: () => number;
}

export interface PeerView {
  did: DID;
  handle: string;
  tier: TrustTier;
  tierName: string;
  sessionOpen: boolean;
}

export interface RequestView {
  did: DID;
  handle: string;
  requestedTier: TrustTier;
}

export interface ActivityItem {
  at: number;
  text: string;
}

export interface AgentState {
  handle?: string;
  did?: DID;
  connected: boolean;
  peers: PeerView[];
  requests: RequestView[];
  activity: ActivityItem[];
  availability: string[];
  pairingCode?: { code: string; tier: TrustTier; inviteUrl: string };
}

interface IncomingRequest {
  card: SignedAgentCard;
  requestedTier: TrustTier;
  codeProof?: string;
}

export class AgentService {
  private readonly directory: DirectoryClient;
  private readonly connectToRelay: () => ClientTransport;
  private readonly now: () => number;

  private identity?: Identity;
  private card?: SignedAgentCard;
  private client?: AlinqClient;

  private readonly issuedCodes: PairingCode[] = [];
  private readonly incoming = new Map<DID, IncomingRequest>();
  private readonly handleByDid = new Map<DID, string>();
  private readonly relationships = new Map<DID, SignedRelationship>();
  private availability: string[] = [];
  private activity: ActivityItem[] = [];
  private pairingCode?: AgentState["pairingCode"];

  private readonly listeners = new Set<(s: AgentState) => void>();

  constructor(deps: AgentServiceDeps) {
    this.directory = deps.directory;
    this.connectToRelay = deps.connectToRelay;
    this.now = deps.now ?? (() => Date.now());
  }

  // --- subscription ---------------------------------------------------------

  subscribe(listener: (s: AgentState) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  getState(): AgentState {
    return this.snapshot();
  }

  private emit(): void {
    const s = this.snapshot();
    for (const l of this.listeners) l(s);
  }

  private snapshot(): AgentState {
    const peers: PeerView[] = (this.client?.peers() ?? []).map((p) => ({
      did: p.peerDID,
      handle: this.handleByDid.get(p.peerDID) ?? p.peerDID,
      tier: p.tier,
      tierName: TRUST_TIER_NAMES[p.tier],
      sessionOpen: p.sessionOpen,
    }));
    const requests: RequestView[] = [...this.incoming.entries()].map(([did, r]) => ({
      did,
      handle: r.card.handle,
      requestedTier: r.requestedTier,
    }));
    return {
      handle: this.card?.handle,
      did: this.identity?.did,
      connected: this.client !== undefined,
      peers,
      requests,
      activity: [...this.activity].reverse(),
      availability: [...this.availability],
      pairingCode: this.pairingCode,
    };
  }

  private log(text: string): void {
    this.activity.push({ at: this.now(), text });
  }

  // --- onboarding -----------------------------------------------------------

  async onboard(username: string, displayName?: string): Promise<{ handle: string; did: DID }> {
    const identity = createIdentity();
    const handle = makeHandle(username, identity.did);
    const card = signCard(
      {
        did: identity.did,
        handle,
        displayName: displayName ?? username,
        capabilities: ["schedule.match"],
        relayEndpoint: "wss://app.alinq.ai/relay",
        publicKeys: { signing: b64uEncode(identity.signPub), keyAgreement: b64uEncode(identity.kaPub) },
      },
      identity.signPriv,
    );
    this.identity = identity;
    this.card = card;
    await this.directory.register(card);
    await this.startClient();
    this.log(`Onboarded as ${handle}; card registered in the directory.`);
    this.emit();
    return { handle, did: identity.did };
  }

  private async startClient(): Promise<void> {
    if (!this.identity || !this.card) throw new Error("onboard first");
    const client = new AlinqClient(this.identity, this.card, this.connectToRelay());
    client.setUnknownSenderResolver((did) => this.directory.resolveDid(did));
    client.onMessage((from, payload) => this.onInbound(from, payload));
    client.onMatchResult((from, _s, intersection) => {
      this.log(`Common free slots with ${this.nameOf(from)}: ${intersection.join(", ") || "none"}.`);
      this.emit();
    });
    await client.connect();
    if (this.availability.length) client.setAvailability(this.availability);
    this.client = client;
  }

  // --- my address + pairing codes ------------------------------------------

  myInviteLink(): string | undefined {
    return this.card ? `https://alinq.ai/${encodeURIComponent(this.card.handle)}` : undefined;
  }

  generatePairingCode(tier: TrustTier, ttlMs = 24 * 60 * 60_000): AgentState["pairingCode"] {
    if (!this.card) throw new Error("onboard first");
    const pc = makePairingCode(tier, ttlMs, this.now(), randomBytes);
    this.issuedCodes.push(pc);
    this.pairingCode = { code: pc.code, tier, inviteUrl: encodeInvite(this.card, pc.code, tier) };
    this.log(`Issued a pairing code granting tier ${tier} (${TRUST_TIER_NAMES[tier]}).`);
    this.emit();
    return this.pairingCode;
  }

  // --- connect flow ---------------------------------------------------------

  /** Connect to a peer by handle or by an alinq:// invite, with a pairing code. */
  async connect(input: string, code: string, requestedTier: TrustTier): Promise<void> {
    const client = this.requireClient();
    let card: SignedAgentCard;
    let effectiveCode = code;
    let tier = requestedTier;
    if (input.startsWith("alinq://invite")) {
      const decoded = decodeInvite(input);
      card = decoded.card;
      effectiveCode = code || decoded.code;
      tier = requestedTier || decoded.tier;
    } else {
      const resolved = await this.directory.resolveHandle(input.trim());
      if (!resolved) throw new Error(`unknown handle: ${input}`);
      card = resolved;
    }
    if (!verifyCard(card)) throw new Error("peer card failed verification");

    this.handleByDid.set(card.did, card.handle);
    client.link(card, tier);
    await client.openSession(card.did);
    const nonce = this.sessionNonce(card.did);
    client.sendLink(card.did, buildLinkRequest(this.requireCard(), tier, effectiveCode, nonce));
    this.log(`Sent a link request to ${card.handle} at tier ${tier} (${TRUST_TIER_NAMES[tier]}).`);
    this.emit();
  }

  acceptRequest(did: DID, grantTier?: TrustTier): void {
    const client = this.requireClient();
    const req = this.incoming.get(did);
    if (!req) throw new Error("no such request");
    const nonce = this.sessionNonce(did);
    const issued = this.issuedCodes.find(
      (c) => req.codeProof && verifyCodeProof(c, nonce, req.codeProof, this.now()),
    );
    const result = acceptLink({
      request: { type: "link.request", from: req.card, requestedTier: req.requestedTier, codeProof: req.codeProof },
      me: this.requireIdentity(),
      ...(issued ? { issuedCode: issued } : {}),
      sessionNonce: nonce,
      now: this.now(),
      ...(grantTier !== undefined ? { grantTier } : {}),
    });
    client.setTier(did, result.grantedTier);
    this.relationships.set(did, result.relationship);
    client.sendLink(did, result.payload);
    this.incoming.delete(did);
    this.log(`Accepted ${req.card.handle} at tier ${result.grantedTier} (${TRUST_TIER_NAMES[result.grantedTier]}).`);
    this.emit();
  }

  declineRequest(did: DID): void {
    const req = this.incoming.get(did);
    this.requireClient().sendLink(did, { type: "link.decline" });
    this.incoming.delete(did);
    if (req) this.log(`Declined ${req.card.handle}.`);
    this.emit();
  }

  downgrade(did: DID, newTier: TrustTier): void {
    const client = this.requireClient();
    const rel = this.relationships.get(did);
    if (!rel) {
      // No stored co-signed record (e.g. we were the requester side); set locally + notify.
      client.setTier(did, newTier);
      client.sendLink(did, { type: "link.revoke", reason: "downgraded" });
    } else {
      const payload = downgradeRelationship(rel, newTier, this.requireIdentity(), this.now());
      client.setTier(did, newTier);
      client.sendLink(did, payload);
    }
    this.log(`Set ${this.nameOf(did)} to tier ${newTier} (${TRUST_TIER_NAMES[newTier]}).`);
    this.emit();
  }

  // --- scheduling -----------------------------------------------------------

  setAvailability(slots: string[]): void {
    this.availability = [...slots];
    this.client?.setAvailability(this.availability);
    this.emit();
  }

  async scheduleWith(did: DID): Promise<string[]> {
    const client = this.requireClient();
    if (!client.peers().find((p) => p.peerDID === did && p.sessionOpen)) {
      await client.openSession(did);
    }
    const slots = await client.scheduleMatch(did);
    this.log(`Ran a zero-knowledge match with ${this.nameOf(did)}: ${slots.join(", ") || "no overlap"}.`);
    this.emit();
    return slots;
  }

  // --- inbound message handling --------------------------------------------

  private onInbound(from: DID, payload: Message): void {
    switch (payload.type) {
      case "link.request": {
        this.handleByDid.set(from, payload.from.handle);
        this.incoming.set(from, {
          card: payload.from,
          requestedTier: payload.requestedTier,
          ...(payload.codeProof !== undefined ? { codeProof: payload.codeProof } : {}),
        });
        this.log(`Link request from ${payload.from.handle} (wants tier ${payload.requestedTier}).`);
        break;
      }
      case "link.accept": {
        const rel = payload.relationship;
        if (rel.sigB === "" && this.identity?.did === rel.b) {
          // We are the requester: counter-sign and confirm back.
          const full = counterSign(rel, this.requireIdentity());
          this.relationships.set(from, full);
          this.requireClient().setTier(from, payload.grantedTier);
          this.requireClient().sendLink(from, { type: "link.accept", grantedTier: payload.grantedTier, relationship: full });
          this.log(`Link confirmed with ${this.nameOf(from)} at tier ${payload.grantedTier}.`);
        } else {
          // We are the acceptor receiving the fully co-signed record.
          this.relationships.set(from, rel);
          this.requireClient().setTier(from, payload.grantedTier);
          this.log(`Relationship with ${this.nameOf(from)} is now co-signed at tier ${payload.grantedTier}.`);
        }
        break;
      }
      case "link.decline": {
        this.log(`${this.nameOf(from)} declined the link request.`);
        break;
      }
      case "link.revoke": {
        this.requireClient().setTier(from, 0);
        this.log(`${this.nameOf(from)} revoked/downgraded the relationship.`);
        break;
      }
      case "link.downgrade": {
        this.requireClient().setTier(from, payload.tier);
        this.relationships.set(from, payload.relationship);
        this.log(`${this.nameOf(from)} set the relationship to tier ${payload.tier}.`);
        break;
      }
      default:
        return; // capability.* / psi.* are handled inside the client
    }
    this.emit();
  }

  // --- helpers --------------------------------------------------------------

  private sessionNonce(peerDID: DID): string {
    return [this.requireIdentity().did, peerDID].sort().join("|");
  }

  private nameOf(did: DID): string {
    return this.handleByDid.get(did) ?? did;
  }

  private requireClient(): AlinqClient {
    if (!this.client) throw new Error("not connected — onboard first");
    return this.client;
  }
  private requireIdentity(): Identity {
    if (!this.identity) throw new Error("no identity — onboard first");
    return this.identity;
  }
  private requireCard(): SignedAgentCard {
    if (!this.card) throw new Error("no card — onboard first");
    return this.card;
  }
}
